import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, X, Calendar, AlertCircle } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeaveRequests = () => {
  const { user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("Pending");

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchLeaves();
  }, []);

  const fetchLeaves = async () => {
    try {
      const response = await axios.get(`${API}/leaves`);
      setLeaves(response.data);
    } catch (error) {
      toast.error("Failed to fetch leave requests");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (leaveId, status) => {
    try {
      await axios.put(`${API}/leaves/${leaveId}?status=${status}`);
      toast.success(`Leave request ${status.toLowerCase()}`);
      fetchLeaves();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update leave request");
    }
  };

  const filteredLeaves = leaves.filter(leave => {
    if (filterStatus === "all") return true;
    return leave.status === filterStatus;
  });

  const pendingCount = leaves.filter(l => l.status === "Pending").length;

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <AlertCircle size={48} className="mb-4 text-slate-300" />
        <p className="text-lg font-medium">Access Restricted</p>
        <p className="text-sm mt-1">Only administrators can approve leave requests.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leaves-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Leave Approvals
          </h1>
          {pendingCount > 0 && (
            <p className="text-sm text-amber-600 mt-1">
              {pendingCount} request{pendingCount > 1 ? 's' : ''} pending your approval
            </p>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-500">Filter:</span>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40 bg-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Pending">Pending ({pendingCount})</SelectItem>
            <SelectItem value="Approved">Approved</SelectItem>
            <SelectItem value="Rejected">Rejected</SelectItem>
            <SelectItem value="all">All Requests</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Leave Requests List */}
      <div className="space-y-3">
        {filteredLeaves.length > 0 ? (
          filteredLeaves.map((leave) => (
            <div
              key={leave.id}
              className="stat-card"
              data-testid={`leave-${leave.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-prepify-50 flex items-center justify-center">
                    <Calendar size={20} className="text-prepify-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-slate-900">{leave.user_name || "Unknown User"}</span>
                      <span className={`badge ${
                        leave.leave_type === "Sick" ? "badge-danger" :
                        leave.leave_type === "Earned" ? "badge-info" : "badge-neutral"
                      }`}>
                        {leave.leave_type}
                      </span>
                      <span className={`badge ${
                        leave.status === "Approved" ? "badge-success" :
                        leave.status === "Rejected" ? "badge-danger" : "badge-warning"
                      }`}>
                        {leave.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      <span className="font-medium">
                        {new Date(leave.start_date).toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric"
                        })}
                      </span>
                      {" → "}
                      <span className="font-medium">
                        {new Date(leave.end_date).toLocaleDateString("en-IN", {
                          day: "numeric", month: "short", year: "numeric"
                        })}
                      </span>
                    </p>
                    <p className="text-sm text-slate-500 mt-2">{leave.reason}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      Requested on {new Date(leave.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Admin Approval Actions */}
                {leave.status === "Pending" && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleUpdateStatus(leave.id, "Approved")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors text-sm font-medium"
                      data-testid={`approve-leave-${leave.id}`}
                    >
                      <Check size={14} /> Approve
                    </button>
                    <button
                      onClick={() => handleUpdateStatus(leave.id, "Rejected")}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors text-sm font-medium"
                      data-testid={`reject-leave-${leave.id}`}
                    >
                      <X size={14} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="stat-card text-center py-12">
            <Calendar size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">
              {filterStatus === "Pending" 
                ? "No pending leave requests" 
                : filterStatus === "all" 
                  ? "No leave requests yet"
                  : `No ${filterStatus.toLowerCase()} requests`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveRequests;
